/**
 * El texto de un producto, sin el HTML con el que lo guarda la tienda.
 *
 * La descripcion de Shopify es HTML que escribe cada comerciante. Pintarlo tal
 * cual en una pagina de Zindra seria ejecutar en nuestro dominio lo que cualquier
 * vendedor pegue en su tienda, asi que se guarda solo el texto, conservando los
 * saltos de parrafo, que es lo unico del formato que importa para leerlo.
 */

const ENTIDADES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  aacute: "á", eacute: "é", iacute: "í", oacute: "ó", uacute: "ú",
  Aacute: "Á", Eacute: "É", Iacute: "Í", Oacute: "Ó", Uacute: "Ú",
  ntilde: "ñ", Ntilde: "Ñ", uuml: "ü", Uuml: "Ü", ccedil: "ç", Ccedil: "Ç",
  agrave: "à", egrave: "è", ograve: "ò", iexcl: "¡", iquest: "¿",
  ordm: "º", ordf: "ª", deg: "°", euro: "€", middot: "·",
  ndash: "–", mdash: "—", hellip: "…", laquo: "«", raquo: "»",
  lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”",
}

function sinEntidades(texto: string): string {
  return texto
    .replace(/&#(\d+);/g, (_, n) => codigo(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => codigo(Number.parseInt(n, 16)))
    .replace(/&([a-zA-Z]+);/g, (todo, nombre) => ENTIDADES[nombre] ?? todo)
}

function codigo(n: number): string {
  if (!Number.isFinite(n) || n <= 0 || n > 0x10ffff) return ""
  try {
    return String.fromCodePoint(n)
  } catch {
    return ""
  }
}

export function textoDeHtml(html: string | null | undefined, tope = 4000): string {
  if (!html) return ""
  const texto = String(html)
    // Lo que no es contenido: estilos, guiones y comentarios, con lo que lleven dentro.
    .replace(/<(script|style|iframe|object|noscript)[\s\S]*?<\/\1\s*>/gi, " ")
    .replace(/<!--[\s\S]*?-->/g, " ")
    // Lo que separa parrafos pasa a ser un salto de linea.
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(p|div|h[1-6]|li|tr|ul|ol|table|section|blockquote)\s*>/gi, "\n\n")
    .replace(/<\s*li[^>]*>/gi, "· ")
    // Las etiquetas que van dentro de una frase se quitan sin dejar hueco: si no,
    // «<strong>Huila</strong>.» acabaria como «Huila .».
    .replace(/<\/?\s*(strong|em|b|i|u|span|a|small|sup|sub|mark)\b[^>]*>/gi, "")
    .replace(/<[^>]*>/g, " ")

  return sinEntidades(texto)
    // Tras quitar entidades puede quedar un «<» suelto: fuera, que esto es texto.
    .replace(/[<>]/g, " ")
    .replace(/[ \t ]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, tope)
    .trim()
}

/** Las primeras frases, para listados y para la descripcion que ven los buscadores. */
export function resumen(texto: string | null | undefined, tope = 160): string {
  const plano = String(texto ?? "").replace(/\s+/g, " ").trim()
  if (plano.length <= tope) return plano
  const corte = plano.slice(0, tope)
  const ultimoEspacio = corte.lastIndexOf(" ")
  return `${corte.slice(0, ultimoEspacio > tope * 0.6 ? ultimoEspacio : tope).trim()}…`
}

/** «19,00 €» en castellano; «€19.00» en inglés. */
export function euros(importe: number | null | undefined, idioma: "es" | "en" = "es"): string {
  const valor = Number(importe) || 0
  return new Intl.NumberFormat(idioma === "en" ? "en-GB" : "es-ES", { style: "currency", currency: "EUR" }).format(valor)
}
